import '../css/month-by-month-trends.page.css';

import $ from 'jquery';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
Exporting(Highcharts);
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
NoDataToDisplay(Highcharts);
import Accessibility from 'highcharts/modules/accessibility';
Accessibility(Highcharts);
import { appConstants } from './app-constants';
import { configureCharts } from './chart-config';
import { Tab, Popover, Tooltip } from 'bootstrap';
import { showLoadingSpinner, hideLoadingSpinner } from './loading';
import CustomSelect from './custom-select';
import RequestManager from './request-manager';
import { showNoData, buildChartColors, sortGroupedData as sortGroupedDataUtil, getAgeGroupOrderValue as getAgeGroupOrderValueUtil } from './chart-utils';

class MonthByMonthTrendsPage {

    filters = {
        state: 0,
        metroArea: 0,
        vaccine: 0,
        ageGroup: 0,
        gender: 0,
        ethnicity: 0,
        timePeriod: 0,
        gapTimePeriod: 0,
        gapTimePeriod2: 0,
        payer: 0
    }

    currentTab = 'gender';
    gapPopover = null;
    currentGapValue = '';

    viewModel = null;

    requestManager = new RequestManager();

    constructor() {
        // View model is initialized on script for page
        this.viewModel = viewModel;
        this.init();
    }

    init() {
        configureCharts(Highcharts);
        this.populateFilterState();
        this.setDefaultFilters();
        this.populateFilters();
        
        // Show chart loading spinners immediately
        this.showInitialLoadingSpinners();
        
        this.populateMetroAreaFromState();
        this.addTabChangeHandler();
        this.populateTabState();
        this.populateData();
        this.addFilterChangeHandlers();
        this.initializeTooltips();
        this.initializeGapPopover();
        this.updateLabels();
        this.initializeCustomSelects();
        
        // Check initial state of timePeriod filter
        this.updateTimePeriodFilterState();
    }

    initializeTooltips() {
        new Tooltip(document.getElementById('metroAreaTooltip'));
        new Tooltip(document.getElementById('ageTabTooltip'));
        new Tooltip(document.getElementById('ethnicityTabTooltip'));
    }

    initializeCustomSelects() {
        // Create mapping of element IDs to their true default values from setDefaultFilters
        const trueDefaults = {
            'stateFilter': appConstants.national,
            'ageGroupFilter': appConstants.ageGroup,
            'vaccineFilter': appConstants.influenza,
            'timePeriodFilter': appConstants.timePeriod,
            'metroAreaFilter': 0, // Default for metro area (not set in setDefaultFilters)
            'genderFilter': 0, // Default for gender (not set in setDefaultFilters)
            'ethnicityFilter': 0, // Default for ethnicity (not set in setDefaultFilters)
            'payerFilter': 0 // Default for payer (not set in setDefaultFilters)
        };
        
        // Initialize custom selects with true default values for change detection
        this.customSelectInstances = CustomSelect.initializeAll('select[data-custom-select]', {}, trueDefaults);
    }

    showInitialLoadingSpinners() {
        // Show loading spinners for all charts immediately during initialization
        this.showChartLoadingSpinners();
    }

    showChartLoadingSpinners() {
        // Show loading spinners for all charts
        showLoadingSpinner('#gap', '5rem');
        showLoadingSpinner('#gapChart');
        showLoadingSpinner('#age', '5rem');
        showLoadingSpinner('#totalAgeChart');
        showLoadingSpinner('#gender', '5rem');
        showLoadingSpinner('#totalGenderChart');
        showLoadingSpinner('#ethnicity', '5rem');
        showLoadingSpinner('#totalEthnicityChart');
        showLoadingSpinner('#payer', '5rem');
        showLoadingSpinner('#totalPayerChart');
        showLoadingSpinner('#point-of-access', '5rem');
        showLoadingSpinner('#totalPointOfAccessChart');
    }



    setDefaultFilters() {
        if (!this.filters.state) {
            this.filters.state = appConstants.national;
        }
        if (!this.filters.ageGroup) {
            this.filters.ageGroup = appConstants.ageGroup;
        }
        if (!this.filters.vaccine) {
            this.filters.vaccine = appConstants.influenza;
        }
        if (!this.filters.timePeriod) {
            this.filters.timePeriod = appConstants.timePeriod;
        }
        if (!this.filters.gapTimePeriod) {
            this.filters.gapTimePeriod = this.filters.timePeriod;
        }
        if (!this.filters.gapTimePeriod2) {
            this.filters.gapTimePeriod2 = this.filters.timePeriod - 1;
        }
    }

    populateFilters() {
        const keys = Object.keys(this.filters);
        keys.forEach(key => {
            const selector = `#${key}Filter`;
            if (this.filters[key]) {
                $(selector).val(this.filters[key]);
            }
        });
    }

    populateData() {
        this.updateLabels();
        this.populateCharts();
    }

    populateState() {
        // Reset metro area to "All" and repopulate based on selected state
        this.loadMetroAreaOptions(this.filters.state, { resetMetro: true, triggerCharts: true });
    }

    populateMetroAreaFromState() {
        // Initialize metro area dropdown based on current state (for page load/URL parameters)
        this.loadMetroAreaOptions(this.filters.state, { resetMetro: false, triggerCharts: false });
    }

    populateTabState() {
        const searchParams = new URLSearchParams(window.location.search);
        const currTab = searchParams.get('tab');
        if (currTab) {
            this.currentTab = currTab;
        }
        const tabEl = document.querySelector(`#${this.currentTab}-tab`);
        const tab = new Tab(tabEl);
        tab.show();
    }

    addTabChangeHandler() {
        $('button[data-bs-toggle="tab"]').on('shown.bs.tab', event => {
            const tab = $(event.target).attr('aria-controls');
            this.currentTab = tab;
            $('.sidebar-tab-content .tab-pane').removeClass('active');
            $(`#sidebar-${tab}`).addClass('active');
            const url = new URL(window.location.href);
            url.searchParams.set('tab', tab);
            
            // Hide gap popover when switching away from gap tab
            if (this.gapPopover && tab !== 'gap') {
                this.gapPopover.hide();
            }
            
            // Update timePeriod filter state based on current tab
            this.updateTimePeriodFilterState();
            
            window.history.replaceState(null, null, url);
            this.updateLabels();
        });
    }

    addFilterChangeHandlers() {
        const keys = Object.keys(this.filters);
        keys.forEach(key => {
            const selector = `#${key}Filter`;
            $(selector).on('change', e => {
                this.handleFilterSelect(key, e.currentTarget.value);
            });
        });
    }

    handleFilterSelect(key, value) {
        this.filters[key] = parseInt(value);

        const url = new URL(window.location.href);
        url.searchParams.set(key, value);
        window.history.replaceState(null, null, url);

        if (key === 'vaccine') {
            this.populateData();
        } else if (key === 'timePeriod') {
            // Sync gap filters when main time period changes
            const selectedYear = parseInt(value);
            const firstOption = this.viewModel.timePeriodList[0];
            
            if (selectedYear === firstOption) {
                // If user selects first option, set gapTimePeriod2 to that year and gapTimePeriod to year + 1
                this.filters.gapTimePeriod2 = selectedYear;
                this.filters.gapTimePeriod = selectedYear + 1;
            } else {
                // Normal behavior: gapTimePeriod = selected year, gapTimePeriod2 = year - 1
                this.filters.gapTimePeriod = selectedYear;
                this.filters.gapTimePeriod2 = selectedYear - 1;
            }
            
            url.searchParams.set('gapTimePeriod', this.filters.gapTimePeriod);
            url.searchParams.set('gapTimePeriod2', this.filters.gapTimePeriod2);
            this.updateGapPopoverContent();
            this.populateCharts();
            
            // Update timePeriod filter state after sync
            this.updateTimePeriodFilterState();
        } else { // populate charts is done through age filter for vaccines
            if (key === 'state') {
                this.populateState();
            } else if (key === 'metroArea') {
                this.populateCharts();
            } else {
                this.populateCharts();
            }
        }
        
        this.updateLabels();
    }

    populateFilterState() {
        let filterParams = new URLSearchParams(window.location.search);
        filterParams.forEach((value, key) => {
            if (this.filters.hasOwnProperty(key)) {
                this.filters[key] = parseInt(value);
            }
        });
    }

    generateFilterPayload(isGap = false) {
        return {
            yearId: isGap ? this.filters.gapTimePeriod : this.filters.timePeriod,
            prevYearId: isGap ? this.filters.gapTimePeriod2 : 0,
            monthId: 0,
            vaccineId: this.filters.vaccine,
            stateId: this.filters.metroArea && this.filters.metroArea !== 0 ? this.filters.metroArea : this.filters.state,
            genderId: this.filters.gender,
            ageRangeId: 0,
            ageGroupId: this.filters.ageGroup,
            ethnicityId: this.filters.ethnicity,
            pointOfAccessId: 0,
            payTypeId: this.filters.payer,
            projected: 0,
            month: 0
        }
    }

    // DRY helper to populate the Metro Area dropdown with abortable request and stale guards
    loadMetroAreaOptions(stateId, { resetMetro = false, triggerCharts = false } = {}) {
        if (resetMetro) {
            this.filters.metroArea = 0;
        }

        if (stateId === 0 || stateId === appConstants.national) {
            $('#metroAreaFilter').html('<option value="0">All</option>').prop('disabled', true).val(0);
            this.updateLabels();
            if (triggerCharts) {
                this.populateCharts();
            }
            return;
        }

        $('#metroAreaFilter').prop('disabled', false);
        const url = `/MonthByMonthTrends/PopulateMSAList`;
        this.requestManager.start('msaList', url, null, (results) => {
            let html = '<option value="0">All</option>';
            results.forEach(result => {
                html += `<option value="${result.msaId}">${result.description}</option>`;
            });
            $('#metroAreaFilter').html(html);
            $('#metroAreaFilter').val(this.filters.metroArea);
            this.updateLabels();
            if (triggerCharts) {
                this.populateCharts();
            }
        }, {
            type: 'GET',
            data: { value: stateId },
            error: () => {
                $('#metroAreaFilter').html('<option value="0">All</option>').prop('disabled', true).val(0);
                if (resetMetro) {
                    this.filters.metroArea = 0;
                }
                this.updateLabels();
                if (triggerCharts) {
                    this.populateCharts();
                }
            }
        });
    }

    updateLabels() {
        const vaccineName = this.getSelectedVaccineName();
        const locationName = this.getSelectedLocationName();
        const yearText = this.getSelectedYearText();
        
        // Handle gap tab differently
        if (this.currentTab === 'gap') {
            const gapYearText = `${this.filters.gapTimePeriod} vs ${this.filters.gapTimePeriod2}`;
            $('#totalGapLabel').text(`Difference, ${gapYearText}`);
        } else {
            // Check if vaccine name already starts with "Total" to avoid duplication
            const labelPrefix = vaccineName.toLowerCase().startsWith('total') ? '' : 'Total ';
            const labelText = `${labelPrefix}${vaccineName} Vaccinations, ${locationName}, ${yearText}`;
            
            $('#totalAgeLabel').text(labelText);
            $('#totalGenderLabel').text(labelText);
            $('#totalEthnicityLabel').text(labelText);
            $('#totalPayerLabel').text(labelText);
            $('#totalPOALabel').text(labelText);
        }
        
        this.updateBreakdownLabels();
    }

    updateBreakdownLabels() {
        // Update breakdown labels based on current tab
        $('#ageBreakdownLabel').text('Age Breakdown');
        $('#genderBreakdownLabel').text('Gender Breakdown');
        $('#ethnicityBreakdownLabel').text('Race/Ethnicity Breakdown');
        $('#payerBreakdownLabel').text('Payer Breakdown');
        $('#pointOfAccessBreakdownLabel').text('Point-of-Access Breakdown');
    }

    getSelectedVaccineName() {
        const selectedOption = $('#vaccineFilter option:selected');
        return selectedOption.text();
    }

    getSelectedLocationName() {
        const stateOption = $('#stateFilter option:selected');
        const stateName = stateOption.text();
        
        const metroAreaOption = $('#metroAreaFilter option:selected');
        const metroAreaName = metroAreaOption.text();
        
        if (this.filters.metroArea && this.filters.metroArea !== 0 && metroAreaName && metroAreaName !== 'All') {
            return metroAreaName;
        }
        
        return stateName;
    }

    getSelectedYearText() {
        return this.filters.timePeriod.toString();
    }

    populateCharts() {
        // Show loading spinners when charts are being repopulated (e.g., filter changes)
        this.showChartLoadingSpinners();
        
        this.populateGapTotalChart();
        this.populateGapGenderChart();
        this.populateGapAgeChart();
        this.populateGapEthnicityChart();
        this.populateGapPayerChart();
        this.populateGapPointOfAccessChart();
    }

    populateGapTotalChart() {
        const url = `/api/TrendsCharts/Totals`;
        
        // Reset gap value and show loading placeholder in breakdown label
        this.currentGapValue = '';
        $('#gapValue').text('--');
        
        this.requestManager.start('gapTotals', url, this.generateFilterPayload(true), (data) => {
            hideLoadingSpinner('#gap');
            hideLoadingSpinner('#gapChart');
            
            if (data.length > 0) {
                const chartOptions = {
                    title: {
                        text: ''
                    },
                    tooltip: {
                        useHTML: true,
                        formatter: function () {
                            const currentYear = this.points[0].point.realValue;
                            const previousYear = this.points[1].point.realValue;
                            const diffValue = this.points[2].point.realValue;
                            
                            const hasLowValues = (currentYear >= 1 && currentYear <= 9) || (previousYear >= 1 && previousYear <= 9);
                            
                            const percent = previousYear > 0 ? (diffValue / previousYear) * 100 : 0;
                            return `
                            <h6 class="chart-tooltip-header">${this.x}</h6>
                            <div class="chart-tooltip-body">
                                ${this.points.map(point => {
                                    let displayValue;
                                    const realValue = point.point.realValue;
                                    if (point.series.name === 'Difference') {
                                        displayValue = hasLowValues ? 'N/A' : realValue.toLocaleString('en-US');
                                    } else {
                                        displayValue = (realValue >= 1 && realValue <= 9) ? '< 10' : realValue.toLocaleString('en-US');
                                    }
                                    
                                    return `
                                        <div class="chart-tooltip-item">
                                            <span class="chart-tooltip-item-bullet" style="color:${point.color};">●</span>
                                            <span class="chart-tooltip-item-name">${point.series.name}</span>
                                            <span class="chart-tooltip-item-value">${displayValue}</span>
                                        </div>
                                    `;
                                }).join('')}
                                <div class="chart-tooltip-item">
                                    <span class="chart-tooltip-item-name">% Difference</span>
                                    <span class="chart-tooltip-item-value">${percent.toLocaleString('en-US', { maximumFractionDigits: 0 }) + '%'}</span>
                                </div>
                            </div>
                        `;
                        },
                        shared: true
                    },
                    xAxis: {
                        categories: data.map(d => appConstants.monthLabels[d.month - 1]),
                    },
                    // Compute axis max ensuring the primary y-axis always spans at least 0–20
                    // Uses plotted values (1 for 1–9) to match visual scale
                    
                    yAxis: [{
                        alignTicks: false,
                        title: {
                            text: 'Total vaccinations'
                        },
                        allowDecimals: false,
                        min: 0,
                        max: (() => {
                            const values = data.flatMap(d => {
                                const current = (d.currentYearProjected >= 1 && d.currentYearProjected <= 9) ? 1 : d.currentYearProjected;
                                const previous = (d.previousYearProjected >= 1 && d.previousYearProjected <= 9) ? 1 : d.previousYearProjected;
                                return [current, previous];
                            });
                            const maxVal = values.length ? Math.max(...values) : 0;
                            return Math.max(20, maxVal);
                        })()
                    }, {
                        gridLineWidth: 0,
                        title: {
                            text: 'Year over year difference'
                        },
                        allowDecimals: false,
                        opposite: true,
                        plotLines: [{
                            width: 2,
                            value: 0,
                            zIndex: 4
                        }]
                    }],
                    plotOptions: {
                        series: {
                            marker: {
                                symbol: 'circle'
                            }
                        }
                    },
                    series: [{
                        type: 'line',
                        zIndex: 3,
                        name: `${this.filters.gapTimePeriod}`,
                        color: '#337357',
                        data: data.map(d => {
                            return {
                                name: appConstants.monthLabels[d.month - 1],
                                y: (d.currentYearProjected >= 1 && d.currentYearProjected <= 9) ? 1 : d.currentYearProjected,
                                realValue: d.currentYearProjected
                            };
                        })
                    }, {
                        type: 'line',
                        zIndex: 2,
                        name: `${this.filters.gapTimePeriod2}`,
                        color: '#99D17B',
                        data: data.map(d => {
                            return {
                                name: appConstants.monthLabels[d.month - 1],
                                y: (d.previousYearProjected >= 1 && d.previousYearProjected <= 9) ? 1 : d.previousYearProjected,
                                realValue: d.previousYearProjected
                            };
                        })
                    }, {
                        type: 'column',
                        name: `Difference`,
                        color: '#7AE0F9',
                        yAxis: 1,
                        zIndex: 1,
                        data: data.map(d => {
                            const hasLowValues = (d.currentYearProjected >= 1 && d.currentYearProjected <= 9) || (d.previousYearProjected >= 1 && d.previousYearProjected <= 9);
                            return {
                                name: appConstants.monthLabels[d.month - 1],
                                y: hasLowValues ? 1 : d.diffProjected,
                                realValue: d.diffProjected
                            };
                        })
                    }]
                };
                Highcharts.chart('gap', chartOptions);
            }
            else {
                showNoData('gap');
            }
            this.populateGapChart(data);
        });
    }

    populateGapAgeChart() {
        const url = `/api/TrendsCharts/Age`;
        this.requestManager.start('gapAge', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#age');
            hideLoadingSpinner('#totalAgeChart');
            this.renderTrendChart('age', data, 'ageGroupName');
            const groupedByAge = this.formatData(data, 'ageGroupName');
            this.populateTotalAgeChart(groupedByAge);
        });
    }

    populateGapGenderChart() {
        const url = `/api/TrendsCharts/Gender`;
        this.requestManager.start('gapGender', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#gender');
            hideLoadingSpinner('#totalGenderChart');
            this.renderTrendChart('gender', data, 'genderName');
            const groupedByGender = this.formatData(data, 'genderName');
            this.populateTotalGenderChart(groupedByGender);
        });
    }

    populateGapEthnicityChart() {
        const url = `/api/TrendsCharts/Ethnicity`;
        this.requestManager.start('gapEthnicity', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#ethnicity');
            hideLoadingSpinner('#totalEthnicityChart');
            this.renderTrendChart('ethnicity', data, 'ethnicityName');
            const groupedByEthnicity = this.formatData(data, 'ethnicityName');
            this.populateTotalEthnicityChart(groupedByEthnicity);
        });
    }

    populateGapPayerChart() {
        const url = `/api/TrendsCharts/Payor`;
        this.requestManager.start('gapPayer', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#payer');
            hideLoadingSpinner('#totalPayerChart');
            this.renderTrendChart('payer', data, 'payorName');
            const filteredData = data.filter(item => item.payorId !== appConstants.medicaidTotal); // Filter out Medicaid Total only in the breakdown chart
            const groupedByPayer = this.formatData(filteredData, 'payorName');
            this.populateTotalPayerChart(groupedByPayer);
        });
    }

    populateGapPointOfAccessChart() {
        const url = `/api/TrendsCharts/PointOfAccess`;
        this.requestManager.start('gapPOA', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#point-of-access');
            hideLoadingSpinner('#totalPointOfAccessChart');
            this.renderTrendChart('point-of-access', data, 'pointOfAccessName');
            const groupedByPOA = this.formatData(data, 'pointOfAccessName');
            this.populateTotalPointOfAccessChart(groupedByPOA);
        });
    }

    populateGapChart(data) {
        const totalGap = data.reduce((sum, d) => sum + d.diffProjected, 0);
        const totalProj = data.reduce((sum, d) => sum + d.previousYearProjected, 0);
        const percentage = totalProj !== 0 ? (totalGap / totalProj) * 100 : 0;
        const chartData = [{
            name: 'Difference',
            color: '#7AE0F9',
            y: Math.abs(percentage)
        },
        {
            name: '', // + data.timePeriod2 + ' year projected',
            color: '#E7E5E5',
            y: 100 - Math.abs(percentage),
            showInLegend: false
        }];

        // Store and display the formatted gap value
        this.currentGapValue = totalGap <= 0 ? totalGap.toLocaleString('en-US') : '+' + totalGap.toLocaleString('en-US');
        $('#gapValue').text(this.currentGapValue);
        
        // Update the gap breakdown label with the new value
        if (this.currentTab === 'gap') {
            this.updateBreakdownLabels();
        }

        const chartOptions = {
            chart: { spacing: 0 },
            title: {
                verticalAlign: 'middle',
                floating: true,
                text: `${percentage <= 0 ? percentage.toFixed() : '+' + percentage.toFixed()}%`,
                style: {
                    fontSize: '16px'
                },
                y: 0
            },
            plotOptions: {
                pie: {
                    innerSize: '60%',
                    states: {
                        hover: {
                            enabled: false
                        },
                        inactive: {
                            opacity: 1
                        }
                    }
                }
            },
            tooltip: {
                outside: true,
                useHTML: true,
                formatter: function () {
                    if (this.point.index > 0 && percentage > 0 || this.point.index == 0 && percentage < 0) {
                        return false;
                    } else {
                        return `
                             <h6 class="chart-tooltip-header" > ${this.series.name}</h6 >
                             <div class="chart-tooltip-body">
                                 <div class="chart-tooltip-item">
                                         <span class="chart-tooltip-item-value">${new Intl.NumberFormat("en-US", { signDisplay: "exceptZero", maximumFractionDigits: 1 }).format(percentage)}%</span>

                                 </div>
                             </div>
                          `;

                    }
                }
            },
            series: [{
                type: 'pie',
                name: 'Difference',
                data: percentage >= 0 ? chartData : chartData.reverse()
            }]
        };
        Highcharts.chart('gapChart', chartOptions);
    }

    // Helper method to check if data has sufficient projections
    hasValidProjections(data) {
        for (var i = 0; i < data.length; i++) {
            if (data[i].projected >= 10) {
                return true;
            }
        }
        return false;
    }

    // Helper method to render line/column charts for trends
    renderTrendChart(containerId, data, groupKey) {
        const formattedData = this.getSortedGroups(data, groupKey);
        // Precompute the months represented on the x-axis to align series data
        const categoriesMonths = this.formatData(data, 'month').map(d => parseInt(d['month']));

        // Compute axis max ensuring the y-axis always spans at least 0–20
        const projectedValues = formattedData
            .flatMap(group => group.data.map(d => {
                if (d.projected === 0) { return null; }
                return (d.projected >= 1 && d.projected <= 9) ? 1 : d.projected;
            }))
            .filter(v => v !== null);
        const dataMax = projectedValues.length ? Math.max(...projectedValues) : 0;
        const axisMax = Math.max(20, dataMax);

        const chartOptions = {
            title: {
                text: ''
            },
            yAxis: {
                title: { text: 'Total vaccinations' },
                allowDecimals: false,
                min: 0,
                max: axisMax
            },
            xAxis: {
                categories: categoriesMonths.map(m => appConstants.monthLabels[m - 1]),
            },
            plotOptions: {
                series: {
                    connectNulls: true,
                    marker: {
                        symbol: 'circle'
                    }
                }
            },
            responsive: {
                rules: [{
                    condition: {
                        minWidth: 575.98,
                    },
                    chartOptions: {
                        legend: {
                            align: 'right',
                            verticalAlign: 'middle',
                            layout: 'vertical',
                            itemMarginTop: 8,
                            itemMarginBottom: 8
                        }
                    }
                }]
            },
            tooltip: this.getTrendTooltipConfig(),
            series: (() => {
                // Create flat data for color assignment based on total values
                const flatData = formattedData.map(group => ({
                    [groupKey]: group[groupKey],
                    projectedValue: group.data.reduce((sum, d) => sum + d.projected, 0)
                }));
                
                const colors = buildChartColors(flatData, groupKey);
                
                return formattedData.map((group, index) => {
                    // Build a lookup from month -> data for alignment
                    const monthToData = {};
                    group.data.forEach(d => { monthToData[d.month] = d; });
                    const alignedData = categoriesMonths.map(m => {
                        const d = monthToData[m];
                        if (!d) { return null; }
                        // Filter out zero values by rendering them as nulls (gaps)
                        if (d.projected === 0) { return null; }
                        return {
                            name: appConstants.monthLabels[d.month - 1],
                            y: (d.projected >= 1 && d.projected <= 9) ? 1 : d.projected
                        };
                    });
                    const seriesObj = {
                        type: 'line',
                        name: group[groupKey],
                        color: colors[index],
                        data: alignedData
                    };

                    // Only for payer chart, attach payorId so tooltip can fix Total
                    if (groupKey === 'payorName' && group.data && group.data.length) {
                        seriesObj.custom = { payorId: group.data[0].payorId };
                    }

                    return seriesObj;
                });
            })()
        };
        Highcharts.chart(containerId, chartOptions);
    }

    // Helper method to get tooltip configuration
    getTrendTooltipConfig() {
        return {
            useHTML: true,
            formatter: function () {
                let total = 0;

                if (this.points !== undefined) {
                    for (let i = 0; i < this.points.length; i++) {
                        if (this.points[i].y >= 10) {
                            total += this.points[i].y;
                        }
                    }

                    // Correct Medicaid double-count ONLY when this is the payer chart tooltip
                    const payerPoints = this.points.filter(p =>
                        p.series && p.series.options && p.series.options.custom && p.series.options.custom.payorId !== undefined
                    );

                    if (payerPoints.length) {
                        const hasMedicaidTotal = payerPoints.some(p =>
                            p.series.options.custom.payorId === appConstants.medicaidTotal
                        );

                        if (hasMedicaidTotal) {
                            const partsSum = payerPoints
                                .filter(p => {
                                    const id = p.series.options.custom.payorId;
                                    return (id === appConstants.medicaidManagedCare || id === appConstants.medicaidFFS) && p.y >= 10;
                                })
                                .reduce((sum, p) => sum + p.y, 0);

                            total -= partsSum;
                        }
                    }
                }
                if (total != 0) {
                    return `
                    <h6 class="chart-tooltip-header">${this.x}</h6>
                    <div class="chart-tooltip-body">
                    ${this.points.map(point => {
                        var label = point.y >= 1 && point.y <= 9 ? '< 10' : point.y.toLocaleString('en-US');
                        return `
                            <div class="chart-tooltip-item">
                                <span class="chart-tooltip-item-bullet" style="color:${point.color};">●</span>
                                <span class="chart-tooltip-item-name">${point.series.name}</span>
                                <span class="chart-tooltip-item-value">${label}</span>
                            </div>
                        `;
                    }).join('')}
                    <div class="chart-tooltip-item">
                        <span class="chart-tooltip-item-name">Total</span>
                        <span class="chart-tooltip-item-value">${total.toLocaleString('en-US')}</span>
                    </div>
                </div>
                `;
                } else {
                    return `
                    <h6 class="chart-tooltip-header">${this.x}</h6>
                    <div class="chart-tooltip-body">
                    ${this.points.map(point => {
                        var label = point.y >= 1 && point.y <= 9 ? '< 10' : point.y.toLocaleString('en-US');
                        return `
                            <div class="chart-tooltip-item">
                                <span class="chart-tooltip-item-bullet" style="color:${point.color};">●</span>
                                <span class="chart-tooltip-item-name">${point.series.name}</span>
                                <span class="chart-tooltip-item-value">${label}</span>
                            </div>
                        `;
                    }).join('')}`;
                }
            },
            shared: true
        };
    }

    // Helper: derive a numeric ordering from age group label (e.g., "19-49" -> 19, "75+" -> 75)
    getAgeGroupOrderValue(label) {
        return getAgeGroupOrderValueUtil(label);
    }

    // Helper: sort grouped data array by groupKey (alphabetical, except age groups by numeric order)
    sortGroupedData(groups, groupKey) {
        return sortGroupedDataUtil(groups, groupKey);
    }

    // Helper: format raw rows and return sorted grouped data
    getSortedGroups(rows, groupKey) {
        const grouped = this.formatData(rows, groupKey);
        return sortGroupedDataUtil(grouped, groupKey);
    }

    // Helper method to render pie charts for totals
    renderTotalChart(containerId, data, groupKey) {
        // Sort grouped data according to rules
        const sortedGroups = this.sortGroupedData(data, groupKey);
        // Transform data for color assignment - create flat structure with totals
        const flatData = sortedGroups.map(group => ({
            [groupKey]: group[groupKey],
            projectedValue: group.data.reduce((sum, d) => sum + d.projected, 0)
        }));
        
        const colors = buildChartColors(flatData, groupKey);

        // Filter out groups whose total projected value is exactly zero
        const nonZeroGroups = sortedGroups.filter(group => group.data.reduce((sum, d) => sum + d.projected, 0) !== 0);

        const chartOptions = {
            chart: { spacing: 0 },
            title: {
                text: ''
            },
            plotOptions: {
                pie: {
                    innerSize: '60%'
                }
            },
            tooltip: this.getTotalTooltipConfig(),
            series: [{
                type: 'pie',
                name: 'Total',
                data: nonZeroGroups.map((group, index) => {
                    const projectedValue = group.data.reduce((sum, d) => sum + d.projected, 0);
                    return {
                        name: group[groupKey],
                        y: projectedValue >= 1 && projectedValue <= 9 ? 10 : projectedValue,
                        color: colors[index],
                        custom: {
                            extraInformation: (projectedValue >= 1 && projectedValue <= 9) ? ' < 10' : ': ' + projectedValue.toLocaleString('en-US')
                        }
                    };
                })
            }]
        };
        
        Highcharts.chart(containerId, chartOptions);
    }

    // Helper method to get tooltip configuration for total/pie charts
    getTotalTooltipConfig() {
        return {
            formatter: function () {
                return `<div>
                            ${this.point.name}<br/>
                        </div>
                        <div>
                          <span style="color:${this.point.color}">●&nbsp;</span> ${this.series.name}<b>${this.point.custom.extraInformation}</b> (${Math.round(this.point.percentage)}%)<br/>
                        </div>`;
            }
        };
    }

    populateTotalGenderChart(data) {
        this.renderTotalChart('totalGenderChart', data, 'genderName');
    }

    populateTotalAgeChart(data) {
        this.renderTotalChart('totalAgeChart', data, 'ageGroupName');
    }

    populateTotalEthnicityChart(data) {
        this.renderTotalChart('totalEthnicityChart', data, 'ethnicityName');
    }

    populateTotalPayerChart(data) {
        this.renderTotalChart('totalPayerChart', data, 'payorName');
    }

    populateTotalPointOfAccessChart(data) {
        this.renderTotalChart('totalPointOfAccessChart', data, 'pointOfAccessName');
    }

    formatData(data, key) {
        if (data.length > 0) {
            const groupedData = data.reduce((acc, value) => {
                // Group initialization
                if (!acc[value[key]]) {
                    acc[value[key]] = [];
                }

                // Grouping
                acc[value[key]].push(value);

                return acc;
            }, {});

            return Object.keys(groupedData).map(k => {
                return {
                    [key]: k,
                    data: groupedData[k]
                }
            });
        } else {
            return [];
        }
    }

    initializeGapPopover() {
        // Remove existing popover
        if (this.gapPopover) {
            this.gapPopover.dispose();
        }

        const gapPopoverAnchor = document.querySelector('#gap-popover-anchor');
        const gapTabEl = document.querySelector('#gap-tab');

        this.gapPopover = new Popover(gapPopoverAnchor, {
            html: true,
            customClass: 'gap-popover',
            trigger: 'manual',
            placement: 'bottom',
            content: `
                <button type="button" class="close-button" aria-label="Close">×</button>
                <div class="row align-items-center">
                    <div class="col-auto pe-1">
                        <select id="gapTimePeriodFilter" class="form-select" data-custom-select>${this.renderGapTimePeriodOptions()}</select>
                    </div>
                    <div class="col-auto p-0">
                        <span>vs</span>
                    </div>
                    <div class="col-auto ps-1">
                        <select id="gapTimePeriod2Filter" class="form-select" data-custom-select>${this.renderGapTimePeriod2Options()}</select>
                    </div>
                </div>
            `,
            sanitize: false
        });

        // Handle tab button click to toggle popover when already on gap tab
        $(gapTabEl).on('click', (e) => {
            if (this.currentTab === 'gap') {
                // If already on gap tab, toggle popover
                e.preventDefault();
                e.stopPropagation();
                this.gapPopover.toggle();
            }
        });

        $(gapPopoverAnchor).one('inserted.bs.popover', () => {
            
            // Set current values when popover opens
            $('#gapTimePeriodFilter').val(this.filters.gapTimePeriod);
            $('#gapTimePeriod2Filter').val(this.filters.gapTimePeriod2);
            
            // Initialize custom selects for the popover with true defaults
            const gapTrueDefaults = {
                'gapTimePeriodFilter': appConstants.timePeriod,
                'gapTimePeriod2Filter': appConstants.timePeriod - 1
            };
            this.gapCustomSelectInstances = CustomSelect.initializeAll('#gapTimePeriodFilter, #gapTimePeriod2Filter', {}, gapTrueDefaults);
            
            // Close button handler
            $('.close-button').on('click', () => {
                this.gapPopover.hide();
            });

            $('#gapTimePeriodFilter').on('change', e => {
                const selectedValue = parseInt(e.currentTarget.value);
                this.handleFilterSelect('gapTimePeriod', selectedValue);
                
                // Auto-adjust gapTimePeriod2 if it's >= selected value
                if (this.filters.gapTimePeriod2 >= selectedValue) {
                    const availableOptions = this.viewModel.timePeriodList.filter(x => x < selectedValue);
                    if (availableOptions.length > 0) {
                        const newValue = Math.max(...availableOptions);
                        this.handleFilterSelect('gapTimePeriod2', newValue);
                    }
                }
                
                $('#gapTimePeriod2Filter').html(this.renderGapTimePeriod2Options());
                this.updateLabels();
                
                // Update timePeriod filter state based on gap comparison
                this.updateTimePeriodFilterState();
            });

            $('#gapTimePeriod2Filter').on('change', e => {
                const selectedValue = parseInt(e.currentTarget.value);
                this.handleFilterSelect('gapTimePeriod2', selectedValue);
                
                // Auto-adjust gapTimePeriod if it's <= selected value
                if (this.filters.gapTimePeriod <= selectedValue) {
                    const availableOptions = this.viewModel.timePeriodList.filter(x => x > selectedValue);
                    if (availableOptions.length > 0) {
                        const newValue = Math.min(...availableOptions);
                        this.handleFilterSelect('gapTimePeriod', newValue);
                    }
                }
                
                this.populateYear();
                $('#gapTimePeriodFilter').html(this.renderGapTimePeriodOptions());
                this.updateLabels();
                
                // Update timePeriod filter state based on gap comparison
                this.updateTimePeriodFilterState();
            });
        });

        $(gapPopoverAnchor).on('hidden.bs.popover', () => {
            // Popover hidden
        });
    }

    updateGapPopoverContent() {
        // Update the select values if they exist (popover is open)
        if ($('#gapTimePeriodFilter').length) {
            $('#gapTimePeriodFilter').val(this.filters.gapTimePeriod);
        }
        if ($('#gapTimePeriod2Filter').length) {
            $('#gapTimePeriod2Filter').val(this.filters.gapTimePeriod2);
        }
        // Note: If popover is closed, values will be set when it opens via the 'inserted.bs.popover' event
    }

    // Helper method to check if timePeriod filter should be disabled
    shouldDisableTimePeriodFilter() {
        return this.filters.gapTimePeriod !== this.filters.timePeriod;
    }

    // Helper method to update timePeriod filter state
    updateTimePeriodFilterState() {
        const timePeriodFilter = $('#timePeriodFilter');
        
        // Only disable the filter if we're on the gap tab AND gapTimePeriod !== timePeriod
        if (this.currentTab === 'gap' && this.shouldDisableTimePeriodFilter()) {
            timePeriodFilter.prop('disabled', true);
            // Also disable the custom select if it exists
            if (this.customSelectInstances && this.customSelectInstances['timePeriodFilter']) {
                this.customSelectInstances['timePeriodFilter'].disable();
            }
        } else {
            timePeriodFilter.prop('disabled', false);
            // Also enable the custom select if it exists
            if (this.customSelectInstances && this.customSelectInstances['timePeriodFilter']) {
                this.customSelectInstances['timePeriodFilter'].enable();
            }
        }
    }

    renderGapTimePeriodOptions() {
        const timePeriods = [...this.viewModel.timePeriodList].slice(1); // Remove first option
        return timePeriods.map(timePeriod => {
            if (timePeriod === this.filters.gapTimePeriod) {
                return `<option value="${timePeriod}" selected>${timePeriod}</option>`;
            } else {
                return `<option value="${timePeriod}">${timePeriod}</option>`;
            }
        }).join('');
    }

    renderGapTimePeriod2Options() {
        const timePeriods = [...this.viewModel.timePeriodList].slice(0, -1); // Remove last option
        return timePeriods.map(timePeriod => {
            if (timePeriod === this.filters.gapTimePeriod2) {
                return `<option value="${timePeriod}" selected>${timePeriod}</option>`;
            } else {
                return `<option value="${timePeriod}">${timePeriod}</option>`;
            }
        }).join('');
    }
}

$(() => {
    $('#mainContentLoading').addClass('d-none');
    $('#mainContent').removeClass('d-none');
    new MonthByMonthTrendsPage();
});
